import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, money } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { Download, ShieldCheck, Prohibit, Check, Plus, PencilSimple, ArrowsCounterClockwise, X } from "@phosphor-icons/react";

/**
 * Iter-63.1.10 -- draft-only Edit + Correct-this-invoice (void + new draft)
 * + richer Mark Paid modal (amount / method / reference / paid_at / notes).
 *
 * The signed-hash contract is preserved: only draft edits mutate the
 * canonical body, and mark-paid writes to a `payment` sub-doc that lives
 * outside the signed canonical body.
 */
export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    client_id: "",
    period_start: firstOfMonthISO(),
    period_end: firstOfNextMonthISO(),
  });
  const [editing, setEditing] = useState(null);   // draft being edited
  const [payDialog, setPayDialog] = useState(null); // issued invoice being paid
  const [correctDialog, setCorrectDialog] = useState(null); // issued invoice being corrected

  const load = async () => {
    const { data } = await api.get("/invoices");
    setRows(data.invoices);
  };

  useEffect(() => {
    load();
    api.get("/organizations?type=client&page_size=50").then((r) => {
      setClients(r.data.organizations);
      if (r.data.organizations[0])
        setForm((f) => ({ ...f, client_id: r.data.organizations[0].id }));
    });
  }, []);

  const doPreview = async () => {
    try {
      const { data } = await api.post("/invoices/preview", form);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const generate = async () => {
    try {
      const { data } = await api.post("/invoices/generate", form);
      toast.success(`Draft ${data.invoice_number} created`);
      setShowNew(false); setPreview(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const issue = async (id) => {
    if (!confirm("Issue this invoice? It will be signed and locked.")) return;
    try {
      const { data } = await api.post(`/invoices/${id}/issue`, {
        due_at: addDaysISO(30),
      });
      toast.success(`Issued · signed ${data.signed_hash.slice(0, 12)}…`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const voidInv = async (id) => {
    const reason = prompt("Void reason?");
    if (reason === null) return;
    try {
      await api.post(`/invoices/${id}/void`, { reason });
      toast.success("Voided");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const verify = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}/verify`);
      if (!data.signed) toast.info(data.reason);
      else if (data.matches) toast.success("Signature valid — content untampered");
      else toast.error("SIGNATURE MISMATCH — do not trust this invoice");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = (id) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf`, "_blank");
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Billing</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Invoices</h1>
          <div className="mt-1 text-sm text-zinc-600">
            Delivered leads × campaign price, minus approved return credits. Every issued invoice is HMAC-SHA256 signed.
          </div>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="new-invoice-btn"
          className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
          <Plus size={12} className="inline mr-1" /> {showNew ? "Close" : "New invoice"}
        </button>
      </div>

      {showNew && (
        <div className="brut-card p-5 mb-4 space-y-4" data-testid="invoice-generator">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label-mono">Client*</label>
              <select data-testid="inv-client" className="w-full border border-black px-2 py-1.5 mt-1"
                value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono">Period start</label>
              <input data-testid="inv-start" type="date"
                className="w-full border border-black px-3 py-2 mt-1"
                value={form.period_start.slice(0, 10)}
                onChange={(e) => setForm({ ...form, period_start: `${e.target.value}T00:00:00+00:00` })} />
            </div>
            <div>
              <label className="label-mono">Period end</label>
              <input data-testid="inv-end" type="date"
                className="w-full border border-black px-3 py-2 mt-1"
                value={form.period_end.slice(0, 10)}
                onChange={(e) => setForm({ ...form, period_end: `${e.target.value}T00:00:00+00:00` })} />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={doPreview} data-testid="inv-preview"
              className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white">
              Preview ledger
            </button>
            <button onClick={generate} data-testid="inv-generate" disabled={!preview}
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-60">
              Generate draft
            </button>
          </div>

          {preview && (
            <div className="border border-black p-4 space-y-3" data-testid="inv-preview-panel">
              <div className="text-sm">
                <b>{preview.client.name}</b> · {preview.period_start.slice(0, 10)} → {preview.period_end.slice(0, 10)}
              </div>
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="border-b border-black text-[10px] uppercase tracking-widest">
                    <th className="py-1 text-left">Campaign</th>
                    <th className="py-1 text-right">Leads</th>
                    <th className="py-1 text-right">Unit</th>
                    <th className="py-1 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.line_items.map((li) => (
                    <tr key={li.campaign_id || li.campaign_name} className="border-b border-zinc-200">
                      <td className="py-1">{li.campaign_name}</td>
                      <td className="py-1 text-right">{li.accepted_leads}</td>
                      <td className="py-1 text-right">{money(li.unit_price)}</td>
                      <td className="py-1 text-right">{money(li.subtotal)}</td>
                    </tr>
                  ))}
                  {!preview.line_items.length && <tr><td colSpan={4} className="py-2 text-center label-mono">No delivered leads</td></tr>}
                </tbody>
              </table>
              <div className="text-sm font-mono flex justify-between border-t border-black pt-2">
                <span>Subtotal</span><span>{money(preview.subtotal)}</span>
              </div>
              <div className="text-sm font-mono flex justify-between text-red-700">
                <span>Approved credits ({preview.credits.length})</span>
                <span>-{money(preview.credits_total)}</span>
              </div>
              <div className="font-chivo font-black text-xl flex justify-between border-t-2 border-black pt-2">
                <span>Total due</span><span>{money(preview.total)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="brut-card">
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[900px]">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-4 text-left">Invoice #</th>
              <th className="py-2 px-4 text-left">Client</th>
              <th className="py-2 px-4 text-left">Period</th>
              <th className="py-2 px-4 text-left">Subtotal</th>
              <th className="py-2 px-4 text-left">Credits</th>
              <th className="py-2 px-4 text-left">Total</th>
              <th className="py-2 px-4 text-left">Status</th>
              <th className="py-2 px-4 text-left">Issued</th>
              <th className="py-2 px-4 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-zinc-200 hover:bg-zinc-100" data-testid={`inv-row-${r.id}`}>
                <td className="py-2 px-4 font-semibold">
                  {r.invoice_number}
                  {r.corrects_invoice_number && (
                    <div className="text-[9px] font-mono uppercase tracking-widest text-amber-700"
                         data-testid={`inv-corrects-${r.id}`}>
                      ↳ corrects {r.corrects_invoice_number}
                    </div>
                  )}
                  {r.corrected_by_invoice_number && (
                    <div className="text-[9px] font-mono uppercase tracking-widest text-red-700"
                         data-testid={`inv-corrected-by-${r.id}`}>
                      ↻ replaced by {r.corrected_by_invoice_number}
                    </div>
                  )}
                </td>
                <td className="py-2 px-4">{r.client_name}</td>
                <td className="py-2 px-4 text-[11px]">{r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)}</td>
                <td className="py-2 px-4">{money(r.subtotal)}</td>
                <td className="py-2 px-4 text-red-700">-{money(r.credits_total)}</td>
                <td className="py-2 px-4 font-bold">{money(r.total)}</td>
                <td className="py-2 px-4"><StatusBadge status={r.status} testId={`inv-status-${r.id}`} /></td>
                <td className="py-2 px-4 text-[11px]">{r.issued_at ? fmtDate(r.issued_at) : "—"}</td>
                <td className="py-2 px-4 whitespace-nowrap">
                  {r.status === "draft" && (
                    <>
                      <button onClick={() => setEditing(r)} data-testid={`inv-edit-${r.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                        <PencilSimple size={10} className="inline" /> Edit
                      </button>
                      <button onClick={() => issue(r.id)} data-testid={`inv-issue-${r.id}`}
                        className="border border-black bg-black text-white px-2 py-1 text-[10px] uppercase tracking-widest mr-1">
                        <Check size={10} className="inline" /> Issue
                      </button>
                    </>
                  )}
                  {r.status === "issued" && (
                    <>
                      <button onClick={() => setPayDialog(r)} data-testid={`inv-paid-${r.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                        Paid
                      </button>
                      <button onClick={() => setCorrectDialog(r)} data-testid={`inv-correct-${r.id}`}
                        className="border border-amber-600 text-amber-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-amber-600 hover:text-white mr-1">
                        <ArrowsCounterClockwise size={10} className="inline" /> Correct
                      </button>
                    </>
                  )}
                  {r.pdf_file_id && (
                    <>
                      <button onClick={() => downloadPdf(r.id)} data-testid={`inv-download-${r.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                        <Download size={10} className="inline" /> PDF
                      </button>
                      <button onClick={() => verify(r.id)} data-testid={`inv-verify-${r.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                        <ShieldCheck size={10} className="inline" /> Verify
                      </button>
                    </>
                  )}
                  {r.status !== "void" && r.status !== "paid" && (
                    <button onClick={() => voidInv(r.id)} data-testid={`inv-void-${r.id}`}
                      className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                      <Prohibit size={10} className="inline" /> Void
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="py-6 text-center label-mono">No invoices yet</td></tr>}
          </tbody>
        </table>
        </div>
        {/* Mobile card view */}
        <div className="md:hidden p-2 flex flex-col gap-2" data-testid="invoices-cards">
          {rows.map((r) => (
            <div key={r.id} className="border border-black bg-white p-3" data-testid={`inv-card-${r.id}`}>
              <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                <div className="font-bold text-sm truncate flex-1">{r.invoice_number}</div>
                <StatusBadge status={r.status} testId={`inv-status-${r.id}`} />
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{r.client_name}</div>
              <div className="text-xs text-zinc-700 mt-0.5">{r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)}</div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                <div><div className="label-mono">Subtotal</div>{money(r.subtotal)}</div>
                <div><div className="label-mono">Credits</div><span className="text-red-700">-{money(r.credits_total)}</span></div>
                <div><div className="label-mono">Total</div><span className="font-bold">{money(r.total)}</span></div>
              </div>
              {r.issued_at && <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Issued {fmtDate(r.issued_at)}</div>}
              <div className="mt-2 flex flex-wrap gap-1">
                {r.status === "draft" && (
                  <>
                    <button onClick={() => setEditing(r)} data-testid={`inv-edit-mobile-${r.id}`}
                      className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest">
                      <PencilSimple size={10} className="inline" /> Edit
                    </button>
                    <button onClick={() => issue(r.id)} data-testid={`inv-issue-mobile-${r.id}`}
                      className="border border-black bg-black text-white px-2 py-1 text-[10px] uppercase tracking-widest">
                      <Check size={10} className="inline" /> Issue
                    </button>
                  </>
                )}
                {r.status === "issued" && (
                  <>
                    <button onClick={() => setPayDialog(r)} data-testid={`inv-paid-mobile-${r.id}`}
                      className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                      Paid
                    </button>
                    <button onClick={() => setCorrectDialog(r)} data-testid={`inv-correct-mobile-${r.id}`}
                      className="border border-amber-600 text-amber-700 px-2 py-1 text-[10px] uppercase tracking-widest">
                      Correct
                    </button>
                  </>
                )}
                {r.pdf_file_id && (
                  <button onClick={() => downloadPdf(r.id)} data-testid={`inv-download-mobile-${r.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                    <Download size={10} className="inline" /> PDF
                  </button>
                )}
                {r.status !== "void" && r.status !== "paid" && (
                  <button onClick={() => voidInv(r.id)} data-testid={`inv-void-mobile-${r.id}`}
                    className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest">
                    <Prohibit size={10} className="inline" /> Void
                  </button>
                )}
              </div>
            </div>
          ))}
          {!rows.length && <div className="py-6 text-center label-mono">No invoices yet</div>}
        </div>
      </div>

      {editing && (
        <EditDraftModal
          invoice={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      {payDialog && (
        <MarkPaidModal
          invoice={payDialog}
          onClose={() => setPayDialog(null)}
          onSaved={async () => { setPayDialog(null); await load(); }}
        />
      )}
      {correctDialog && (
        <CorrectInvoiceModal
          invoice={correctDialog}
          onClose={() => setCorrectDialog(null)}
          onSaved={async () => { setCorrectDialog(null); await load(); }}
        />
      )}
    </div>
  );
}


function EditDraftModal({ invoice, onClose, onSaved }) {
  const [items, setItems] = useState(() =>
    (invoice.line_items || []).map((li) => ({
      campaign_id: li.campaign_id || null,
      campaign_name: li.campaign_name || "",
      unit_price: Number(li.unit_price || 0),
      accepted_leads: Number(li.accepted_leads || 0),
    })));
  const [dueAt, setDueAt] = useState(invoice.due_at || "");
  const [periodStart, setPeriodStart] = useState(invoice.period_start || "");
  const [periodEnd, setPeriodEnd] = useState(invoice.period_end || "");
  const [notes, setNotes] = useState(invoice.internal_notes || "");
  const [saving, setSaving] = useState(false);

  const subtotal = useMemo(() =>
    items.reduce((s, i) => s + Number(i.unit_price || 0) * Number(i.accepted_leads || 0), 0),
  [items]);
  const creditsTotal = Number(invoice.credits_total || 0);
  const total = subtotal - creditsTotal;

  const setItem = (idx, patch) =>
    setItems((rows) => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const addRow = () =>
    setItems((rows) => [...rows, { campaign_id: null, campaign_name: "", unit_price: 0, accepted_leads: 0 }]);

  const dropRow = (idx) => setItems((rows) => rows.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${invoice.id}`, {
        line_items: items,
        due_at: dueAt || null,
        period_start: periodStart || null,
        period_end: periodEnd || null,
        internal_notes: notes,
      });
      toast.success(`Saved · ${data.changes ?? 0} field${data.changes === 1 ? "" : "s"} updated`);
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center pt-10 bg-black/50" data-testid="inv-edit-modal">
      <div className="bg-white border-2 border-black brut-shadow-md max-w-3xl w-full mx-4 p-5">
        <div className="flex items-center gap-2 mb-4">
          <PencilSimple size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter">Edit draft · {invoice.invoice_number}</div>
          <button onClick={onClose} data-testid="inv-edit-close"
                  className="ml-auto label-mono hover:text-red-700">Close ✕</button>
        </div>

        <table className="w-full text-xs font-mono" data-testid="inv-edit-lines">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-1 text-left">Campaign</th>
              <th className="py-1 text-right">Unit price</th>
              <th className="py-1 text-right">Accepted</th>
              <th className="py-1 text-right">Subtotal</th>
              <th className="py-1 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((li, i) => (
              <tr key={i} className="border-b border-zinc-200" data-testid={`inv-edit-row-${i}`}>
                <td className="py-1 pr-2">
                  <input value={li.campaign_name}
                    onChange={(e) => setItem(i, { campaign_name: e.target.value })}
                    data-testid={`inv-edit-name-${i}`}
                    className="w-full border border-black px-1 py-0.5" />
                </td>
                <td className="py-1 pr-2 text-right">
                  <input type="number" step="0.01" min="0" value={li.unit_price}
                    onChange={(e) => setItem(i, { unit_price: Number(e.target.value || 0) })}
                    data-testid={`inv-edit-price-${i}`}
                    className="w-24 border border-black px-1 py-0.5 text-right" />
                </td>
                <td className="py-1 pr-2 text-right">
                  <input type="number" step="1" min="0" value={li.accepted_leads}
                    onChange={(e) => setItem(i, { accepted_leads: Number(e.target.value || 0) })}
                    data-testid={`inv-edit-leads-${i}`}
                    className="w-20 border border-black px-1 py-0.5 text-right" />
                </td>
                <td className="py-1 pr-2 text-right"
                    data-testid={`inv-edit-linesubtotal-${i}`}>
                  {money(Number(li.unit_price || 0) * Number(li.accepted_leads || 0))}
                </td>
                <td className="py-1 text-right">
                  <button onClick={() => dropRow(i)} data-testid={`inv-edit-drop-${i}`}
                    className="border border-red-600 text-red-700 px-1.5 text-[10px]">
                    <X size={10} />
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5} className="py-3 text-center label-mono">No line items — add one below.</td></tr>}
          </tbody>
        </table>
        <button onClick={addRow} data-testid="inv-edit-add"
          className="mt-2 border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
          <Plus size={10} className="inline mr-1" /> Add line item
        </button>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <div>
            <label className="label-mono">Due at (YYYY-MM-DD)</label>
            <input type="date" value={String(dueAt || "").slice(0, 10)}
              onChange={(e) => setDueAt(e.target.value)}
              data-testid="inv-edit-due"
              className="w-full border border-black px-2 py-1.5 mt-1" />
          </div>
          <div>
            <label className="label-mono">Period start</label>
            <input type="date" value={String(periodStart || "").slice(0, 10)}
              onChange={(e) => setPeriodStart(`${e.target.value}T00:00:00+00:00`)}
              data-testid="inv-edit-period-start"
              className="w-full border border-black px-2 py-1.5 mt-1" />
          </div>
          <div>
            <label className="label-mono">Period end</label>
            <input type="date" value={String(periodEnd || "").slice(0, 10)}
              onChange={(e) => setPeriodEnd(`${e.target.value}T00:00:00+00:00`)}
              data-testid="inv-edit-period-end"
              className="w-full border border-black px-2 py-1.5 mt-1" />
          </div>
        </div>

        <div className="mt-3">
          <label className="label-mono">Internal notes (optional)</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            data-testid="inv-edit-notes"
            className="w-full border border-black px-2 py-1.5 mt-1 text-sm" />
        </div>

        <div className="mt-4 border-t border-black pt-2 text-sm font-mono flex justify-between">
          <span>Subtotal (computed)</span><span data-testid="inv-edit-subtotal">{money(subtotal)}</span>
        </div>
        <div className="text-sm font-mono flex justify-between text-red-700">
          <span>Credits (from ledger — read-only)</span><span>-{money(creditsTotal)}</span>
        </div>
        <div className="font-chivo font-black text-lg flex justify-between border-t-2 border-black pt-2">
          <span>Total due (computed)</span><span data-testid="inv-edit-total">{money(total)}</span>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} data-testid="inv-edit-cancel"
            className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving} data-testid="inv-edit-save"
            className="bg-black text-white border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40">
            {saving ? "Saving…" : "Save draft edit"}
          </button>
        </div>
      </div>
    </div>
  );
}


function MarkPaidModal({ invoice, onClose, onSaved }) {
  const [amount, setAmount] = useState(Number(invoice.total || 0));
  const [method, setMethod] = useState("ACH");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/invoices/${invoice.id}/mark-paid`, {
        amount_paid: Number(amount),
        payment_method: method,
        transaction_reference: reference,
        paid_at: paidAt ? `${paidAt}T00:00:00+00:00` : null,
        payer_notes: notes,
      });
      toast.success("Marked paid · payment recorded");
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark paid failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center pt-10 bg-black/50" data-testid="inv-paid-modal">
      <div className="bg-white border-2 border-black brut-shadow-md max-w-md w-full mx-4 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Check size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter">Mark paid · {invoice.invoice_number}</div>
          <button onClick={onClose} data-testid="inv-paid-close"
                  className="ml-auto label-mono hover:text-red-700">Close ✕</button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label-mono">Amount paid* (invoice total: {money(invoice.total)})</label>
            <input type="number" step="0.01" min="0" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="inv-paid-amount"
              className="w-full border border-black px-2 py-1.5 mt-1" />
            <div className="text-[10px] text-zinc-500 mt-0.5">
              Partial payments are not supported in the current accounting model. Enter the full total.
            </div>
          </div>
          <div>
            <label className="label-mono">Payment method*</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)}
              data-testid="inv-paid-method"
              className="w-full border border-black px-2 py-1.5 mt-1">
              <option value="ACH">ACH</option>
              <option value="wire">Wire</option>
              <option value="check">Check</option>
              <option value="card">Card</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="label-mono">Transaction reference / confirmation # (optional)</label>
            <input type="text" value={reference} onChange={(e) => setReference(e.target.value)}
              data-testid="inv-paid-reference"
              className="w-full border border-black px-2 py-1.5 mt-1" />
          </div>
          <div>
            <label className="label-mono">Paid at</label>
            <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
              data-testid="inv-paid-date"
              className="w-full border border-black px-2 py-1.5 mt-1" />
          </div>
          <div>
            <label className="label-mono">Payer notes (optional)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="inv-paid-notes"
              className="w-full border border-black px-2 py-1.5 mt-1 text-sm" />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} data-testid="inv-paid-cancel"
            className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving} data-testid="inv-paid-save"
            className="bg-black text-white border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40">
            {saving ? "Saving…" : "Record payment"}
          </button>
        </div>
      </div>
    </div>
  );
}


function CorrectInvoiceModal({ invoice, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (reason.trim().length < 5) {
      toast.error("Please describe the correction (≥5 characters).");
      return;
    }
    if (!confirm(
      `This will void ${invoice.invoice_number} and open a new pre-populated draft. Continue?`,
    )) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/correct`, { reason });
      toast.success(`Voided ${invoice.invoice_number} · new draft ${data.new_draft.invoice_number}`);
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Correction failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center pt-16 bg-black/50" data-testid="inv-correct-modal">
      <div className="bg-white border-2 border-black brut-shadow-md max-w-md w-full mx-4 p-5">
        <div className="flex items-center gap-2 mb-4">
          <ArrowsCounterClockwise size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter">Correct · {invoice.invoice_number}</div>
          <button onClick={onClose} data-testid="inv-correct-close"
                  className="ml-auto label-mono hover:text-red-700">Close ✕</button>
        </div>
        <div className="text-xs text-zinc-700 mb-3">
          This voids the current invoice (signature intact for audit) and creates a fresh draft pre-populated
          from its contents. Review + issue the new draft when ready — nothing is auto-issued.
        </div>
        <div>
          <label className="label-mono">Reason (required, ≥5 chars)</label>
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="inv-correct-reason"
            className="w-full border border-black px-2 py-1.5 mt-1 text-sm" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} data-testid="inv-correct-cancel"
            className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving} data-testid="inv-correct-save"
            className="bg-amber-600 text-white border border-amber-700 px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40">
            {saving ? "Working…" : "Void + create draft"}
          </button>
        </div>
      </div>
    </div>
  );
}


function firstOfMonthISO() {
  const d = new Date();
  d.setUTCDate(1); d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}
function firstOfNextMonthISO() {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() + 1);
  d.setUTCDate(1); d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}
function addDaysISO(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
